/**
 * Contains the types used by the backend API to represent the activity log of the admin section when
 * returning it to the frontend. Instants travel as ISO strings, which is what they arrive as, and the
 * actions travel as the metric names rather than as the enum so that the frontend needs nothing of
 * the database.
 */

/** One changed setting, or one edited field of a user. The wording of `field` is the reader's own. */
export type ActivityLogFieldChange = { field: string; value: unknown }

/**
 * What the Details column of one entry says, beyond the action itself. Each variant belongs to a set
 * of actions and carries only what those actions can explain; `kind` is what the column renders on.
 */
export type ActivityLogDetails =
  | { kind: 'SETTINGS'; changes: ActivityLogFieldChange[] }
  | { kind: 'USER'; name: string | null; changes: ActivityLogFieldChange[] }
  | { kind: 'RESOURCE'; resource: string; name: string | null }
  | { kind: 'FILE_FORMAT'; format: string }
  | { kind: 'SHARE'; name: string | null; accessLevel: string | null }
  | { kind: 'RETENTION'; from: string | null; to: string | null }

export type ActivityLogEntry = {
  id: number
  metric: string
  timestamp: string
  user: { id: number; name: string } | null
  /** A Brief AI member of staff acting for the organization. The real actor is never exposed. */
  performedByBriefAiSupport: boolean
  /** Nobody pressed anything: the retention sweep and the like. */
  performedAutomatically: boolean
  organization: { id: number; name: string } | null
  call: { id: number; name: string } | null
  details: ActivityLogDetails | null
}

/**
 * When each action started being recorded, so that an empty result can be told apart from an action
 * that never happened. Entries themselves are never deleted, so a date here is the whole history.
 */
export type ActivityLogCoverage = {
  /** The earliest of the per-action dates: when this log begins. Null before anything is recorded. */
  loggingSince: string | null
  actions: { metric: string; recordedSince: string | null }[]
}

/** The two actors that are not a person, used as their own values in the Person column's filter. */
export const ACTIVITY_LOG_SUPPORT_PERSON = 'BRIEF_AI_SUPPORT'
export const ACTIVITY_LOG_AUTOMATIC_PERSON = 'AUTOMATIC'
